import _ from "lodash";
import { mobileAppVersionCheck } from "./api/userService";
import { Config } from "./config";
import { isMobileApp, mobileAppInfo } from "./mobileHelper";
import { AppUpdatePromptState, DevicePlatform, FetchState, MobileAppVersion } from "./types";
import { openUrl } from "./urlHelper";

export enum PromptButton {
  Dismissed = "Dismissed",
  NextTime = "Next time",
  Update = "Update"
}

export interface VersionObject {
  major: number;
  minor: number;
  patch: number;
}

const promptButtons = [PromptButton.Dismissed, PromptButton.Update, PromptButton.NextTime];

const isUpdate = (buttonType: PromptButton) => {
  return buttonType === PromptButton.Update;
};

const isDismissed = (buttonType: PromptButton) => {
  return buttonType === PromptButton.Dismissed;
};

const iOS = () => {
  return mobileAppInfo().platform === DevicePlatform.iOS;
};

const android = () => {
  return mobileAppInfo().platform === DevicePlatform.Android;
};

const getAppStoreUrl = (response: MobileAppVersion): string => {
  if (android()) {
    return response.googlePlayStoreUrl;
  }
  if (iOS()) {
    return response.appleStoreUrl;
  }
  return "";
};

const needsUpdate = (newVersion: string) => {
  const currentVersion = Config.Mobile.AppVersion;
  if (!isValidVersion(currentVersion) || !isValidVersion(newVersion)) {
    return false;
  }

  const currentVersionObject = getVersionObject(currentVersion);
  const newVersionObject = getVersionObject(newVersion);
  if (currentVersionObject.major > newVersionObject.major) {
    return false;
  } else if (currentVersionObject.major < newVersionObject.major) {
    return true;
  } else if (currentVersionObject.minor > newVersionObject.minor) {
    return false;
  } else if (currentVersionObject.minor < newVersionObject.minor) {
    return true;
  } else {
    return currentVersionObject.patch < newVersionObject.patch;
  }
};

const isValidVersion = (versionString: string): boolean => {
  if (versionString === undefined || versionString === null || versionString === "") {
    return false;
  }

  const versionArray = versionString.split(".");
  if (versionArray.length !== 3) {
    return false;
  }

  return versionArray.every(v => _.isNumber(Number(v)) && !_.isNaN(Number(v)));
};

const getVersionObject = (versionString: string): VersionObject => {
  const versionArray = versionString.split(".");
  const major = Number(versionArray[0]);
  const minor = Number(versionArray[1]);
  const patch = Number(versionArray[2]);
  return { major, minor, patch };
};

const onButtonPressed = (buttonType: PromptButton, appStoreUrl: string, forceUpdate: boolean) => {
  if (isDismissed(buttonType) && forceUpdate) {
    openAppUpdateRequiredPrompt(appStoreUrl);
  }

  if (isUpdate(buttonType)) {
    openUrl(appStoreUrl);

    // reset the fetchState to force app version check on app resume
    const appUpdateStore = useAppUpdatePromptStore();
    appUpdateStore.fetchState = FetchState.Uninitialized;
  }
};


/**
 * The callback 'onButtonPressed' takes the argument `buttonIndex` _(Number)_, which is the
 * index of the pressed button. Note that the index uses one-based
 * indexing, so the value is `1`, `2`, `3`, etc.
 * @param appStoreUrl string
 * @param buttonLabels []. Options: PromptButton.Update | PromptButton.NextTime
 * @param forceUpdate(optional) boolean. When True, prompt is not dismissable.
 */
const openPrompt = (appStoreUrl: string, buttonLabels, forceUpdate = false) => {
  (window as any)
    .navigator
    .notification
    .confirm(`A new version of ${mobileAppInfo().name} is now available. We recommend you update the app to receive the latest enhancements and bug fixes.`,
    buttonIndex => onButtonPressed(promptButtons[buttonIndex], appStoreUrl, forceUpdate),
    "Update Available",
    buttonLabels
  );
};

const openAppUpdateRequiredPrompt = (appStoreUrl: string) => {
  openPrompt(appStoreUrl, [PromptButton.Update], true);
};

const openAppUpdateSuggestionPrompt = (appStoreUrl: string) => {
  openPrompt(appStoreUrl, [PromptButton.Update, PromptButton.NextTime], false);
};

import { defineStore } from "pinia";

export const initialState: AppUpdatePromptState = {
  canShowPrompt: false,
  fetchState: FetchState.Uninitialized
};

export const useAppUpdatePromptStore = defineStore("appUpdatePrompt", {
  state: () => (_.cloneDeep(initialState)),
  getters: {
    isLoaded: state => state.fetchState === FetchState.Loaded,
  },
  actions: {
    initialize () {
      if (!isMobileApp()) {
        return;
      }

      if (this.fetchState !== FetchState.Uninitialized) {
        return;
      }

      this.fetchState = FetchState.Loading;

      return mobileAppVersionCheck(Config.Mobile.AppId)
      .then(response => {
        const appStoreUrl = getAppStoreUrl(response.data);
        const minRequiredVersion = response.data.minimumRequired;
        const minSuggestedVersion = response.data.minimumSuggested;

        if (needsUpdate(minRequiredVersion)) {
          this.canShowPrompt = true;
          openAppUpdateRequiredPrompt(appStoreUrl);
          return;
        }

        if (needsUpdate(minSuggestedVersion)) {
          this.canShowPrompt = true;
          openAppUpdateSuggestionPrompt(appStoreUrl);
          return;
        }
      })
      .catch(_ => {/** ignore */} )
      .finally(() => {
        this.fetchState = FetchState.Loaded;
      });
    }
  }
});